import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faSyncAlt,
  faAngleLeft,
  faAngleRight,
  faSearch,
  faUpload,
  faHistory,
  faFilter,
  faFileExport,
  faWandMagicSparkles,
  faRobot,
  faXmark,
  faTimes,
  faListAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  getDprValidationProcessedTransactions,
  exportDprValidationProcessedCsv,
  getDprValidationProcessedAiSummary,
} from "../../services/dprValidationUploadApi";

const PER_PAGE_OPTIONS = [10, 25, 50];

const formatDate = (dt) => {
  if (!dt) return "-";
  return new Date(dt).toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderMarkdownAsHtml = (text) =>
  String(text || "")
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/gim, "<em>$1</em>")
    .replace(/^- (.*$)/gim, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br/>");

const StatusBadge = ({ status }) => {
  if (status === "success") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
        <FontAwesomeIcon icon={faCheckCircle} size="xs" />
        Success
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
      <FontAwesomeIcon icon={faTimesCircle} size="xs" />
      Failed
    </span>
  );
};

const DprUploadValidationCsvProcessedPage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("processed_at");
  const [sortDir, setSortDir] = useState("desc");

  // Filters
  const [uploadIdFilter, setUploadIdFilter] = useState("");
  const [transNoFilter, setTransNoFilter] = useState("");
  const [loaNoFilter, setLoaNoFilter] = useState("");
  const [validateStatusFilter, setValidateStatusFilter] = useState("");
  const [validatedByV2Filter, setValidatedByV2Filter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [exportingCsv, setExportingCsv] = useState(false);

  // AI Summary state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiSummaryFilterKey, setAiSummaryFilterKey] = useState("");

  // Applied (only update on Search button / Enter)
  const [applied, setApplied] = useState({
    upload_id: "",
    trans_no: "",
    loa_no: "",
    validate_status: "",
    validated_by_v2: "",
    status: "",
    date_from: "",
    date_to: "",
  });

  const appliedFilterKey = useMemo(() => JSON.stringify(applied), [applied]);
  const hasAppliedFilters = useMemo(
    () =>
      Object.values(applied).some((value) => String(value ?? "").trim() !== ""),
    [applied]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDprValidationProcessedTransactions({
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_direction: sortDir,
        ...applied,
      });
      const d = res.data;
      setRows(d.data || []);
      setTotal(d.total || 0);
      setTotalPages(d.total_pages || 1);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortDir, applied]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleApply = () => {
    setApplied({
      upload_id: uploadIdFilter.trim(),
      trans_no: transNoFilter.trim(),
      loa_no: loaNoFilter.trim(),
      validate_status: validateStatusFilter.trim(),
      validated_by_v2: validatedByV2Filter.trim(),
      status: statusFilter,
      date_from: dateFrom,
      date_to: dateTo,
    });
    setPage(1);
  };

  const handleClear = () => {
    setUploadIdFilter("");
    setTransNoFilter("");
    setLoaNoFilter("");
    setValidateStatusFilter("");
    setValidatedByV2Filter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setApplied({
      upload_id: "",
      trans_no: "",
      loa_no: "",
      validate_status: "",
      validated_by_v2: "",
      status: "",
      date_from: "",
      date_to: "",
    });
    setSortBy("processed_at");
    setSortDir("desc");
    setPage(1);
    setAiSummary(null);
    setAiSummaryFilterKey("");
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await exportDprValidationProcessedCsv({
        ...applied,
        sort_by: sortBy,
        sort_direction: sortDir,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dpr-validation-processed-${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully.");
    } catch {
      toast.error("Failed to export CSV.");
    } finally {
      setExportingCsv(false);
    }
  };

  const handleOpenAiSummary = async (forceRegenerate = false) => {
    if (!hasAppliedFilters) {
      toast.error("Apply at least one filter first before generating AI Summary.");
      return;
    }

    setAiModalOpen(true);
    setAiError("");

    if (!forceRegenerate && aiSummary && aiSummaryFilterKey === appliedFilterKey) {
      return;
    }

    setAiLoading(true);
    try {
      const params = {
        ...applied,
        ...(forceRegenerate ? { regenerate: 1 } : {}),
      };
      const response = await getDprValidationProcessedAiSummary(params);
      setAiSummary({
        ...(response.data?.data || {}),
        source: response.data?.source || "generated",
      });
      setAiSummaryFilterKey(appliedFilterKey);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to generate AI summary.";
      setAiError(message);
      toast.error(message);
    } finally {
      setAiLoading(false);
    }
  };

  const SortIndicator = ({ col }) => {
    if (sortBy !== col) return <span className="ml-1 text-gray-300">[ ]</span>;
    return <span className="ml-1 text-sky-500">{sortDir === "asc" ? "^" : "v"}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Header */}
        <div className="rounded-2xl bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-200 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold tracking-tight">
            (DPR) Upload Validation CSV Processed
          </h1>
          <p className="mt-1 text-sm text-sky-100">
            Detailed row-by-row results for all processed validation CSV uploads.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/dpr-validation-upload")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faUpload} />
              Upload New CSV
            </button>
            <button
              onClick={() => navigate("/dpr-validation-upload-history")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faHistory} />
              Upload History
            </button>
            <button
              onClick={() => navigate("/dpr-validation-upload-ai-summary-history")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faListAlt} />
              AI Summary History
            </button>
            <button
              onClick={handleExportCsv}
              disabled={exportingCsv}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition disabled:opacity-60"
            >
              <FontAwesomeIcon icon={exportingCsv ? faSyncAlt : faFileExport} spin={exportingCsv} />
              {exportingCsv ? "Exporting..." : "Export CSV"}
            </button>
            <button
              onClick={() => handleOpenAiSummary(false)}
              className="group relative ml-auto px-5 py-2.5 rounded-xl font-semibold text-sm text-white
                bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600
                hover:from-purple-500 hover:via-indigo-500 hover:to-blue-500
                shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40
                transition-all duration-300 ease-out hover:scale-[1.03] active:scale-[0.98]
                flex items-center gap-2.5 overflow-hidden"
            >
              <span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
                translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out"
              />
              <span
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                transition-opacity duration-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent"
              />
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                className="relative z-10 text-base group-hover:rotate-12 transition-transform duration-300"
              />
              <span className="relative z-10">AI Summary</span>
              <span className="relative z-10 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
              </span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <FontAwesomeIcon icon={faFilter} />
            Filters
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Upload ID</label>
              <input
                type="text"
                value={uploadIdFilter}
                onChange={(e) => setUploadIdFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                placeholder="uuid..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Trans No.</label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                />
                <input
                  type="text"
                  value={transNoFilter}
                  onChange={(e) => setTransNoFilter(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApply()}
                  placeholder="trans_no..."
                  className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">LOA No.</label>
              <input
                type="text"
                value={loaNoFilter}
                onChange={(e) => setLoaNoFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                placeholder="loa_no..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Validate Status</label>
              <input
                type="text"
                value={validateStatusFilter}
                onChange={(e) => setValidateStatusFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                placeholder="e.g. Validated"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Row Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Validated By V2</label>
              <input
                type="text"
                value={validatedByV2Filter}
                onChange={(e) => setValidatedByV2Filter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                placeholder="username or full name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button
                onClick={handleApply}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                <FontAwesomeIcon icon={faSearch} />
                Search
              </button>
              <button
                onClick={handleClear}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Rows per page:</span>
              {PER_PAGE_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setPerPage(n);
                    setPage(1);
                  }}
                  className={`rounded px-2 py-0.5 font-semibold transition ${
                    perPage === n
                      ? "bg-sky-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-sky-50"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={fetchRows}
                className="ml-2 rounded-lg bg-gray-100 px-2 py-1 text-gray-500 hover:bg-sky-50"
                title="Refresh"
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
              </button>
            </div>
          </div>
          <div className="mt-3 text-xs">
            {hasAppliedFilters ? (
              <p className="text-emerald-600">
                AI Summary will analyze only the currently applied filters.
              </p>
            ) : (
              <p className="text-amber-600">
                Apply at least one filter first, then click AI Summary.
              </p>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-sky-100 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Upload ID</th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort("row_number")}>
                    Row <SortIndicator col="row_number" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("trans_no")}>
                    Trans No. <SortIndicator col="trans_no" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("loa_no")}>
                    LOA No. <SortIndicator col="loa_no" />
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-left" onClick={() => handleSort("validate_status")}>
                    Validate Status <SortIndicator col="validate_status" />
                  </th>
                  <th className="px-4 py-3 text-left">Remarks</th>
                  <th className="px-4 py-3 text-left">Physician</th>
                  <th className="px-4 py-3 text-right">Trans. Amt.</th>
                  <th className="px-4 py-3 text-left">Payment Ref.</th>
                  <th className="px-4 py-3 text-left">Validated By</th>
                  <th className="px-4 py-3 text-left">Validated By V2</th>
                  <th className="cursor-pointer px-4 py-3 text-center" onClick={() => handleSort("status")}>
                    Result <SortIndicator col="status" />
                  </th>
                  <th className="px-4 py-3 text-left">Error</th>
                  <th
                    className="cursor-pointer px-4 py-3 text-left whitespace-nowrap"
                    onClick={() => handleSort("processed_at")}
                  >
                    Processed At <SortIndicator col="processed_at" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 14 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-3 rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="px-4 py-12 text-center text-gray-400">
                      No processed rows found. Apply filters or check the Upload History.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={`${row.status}-${row.id}`} className="hover:bg-gray-50 transition">
                      <td className="max-w-[120px] truncate px-4 py-3 font-mono text-xs text-gray-400" title={row.upload_id}>
                        {row.upload_id ? `${row.upload_id.substring(0, 8)}...` : "-"}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">{row.row_number ?? "-"}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{row.trans_no || "-"}</td>
                      <td className="px-4 py-3 text-gray-600">{row.loa_no || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.validate_status || "-"}</td>
                      <td className="max-w-[160px] truncate px-4 py-3 text-gray-500 text-xs" title={row.remarks || ""}>
                        {row.remarks || "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.physician || "-"}</td>
                      <td className="px-4 py-3 text-right text-gray-700 tabular-nums">
                        {row.transaction_amt != null
                          ? Number(row.transaction_amt).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.payment_ref_no || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">{row.validated_by || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.validated_by_v2_name || row.validated_by_v2_username || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="max-w-[200px] px-4 py-3 text-xs text-red-500" title={row.error_message || ""}>
                        {row.error_message
                          ? row.error_message.length > 50
                            ? `${row.error_message.substring(0, 50)}...`
                            : row.error_message
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                        {formatDate(row.processed_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              <span>
                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`rounded px-2.5 py-1 font-semibold ${
                        p === page ? "bg-sky-600 text-white" : "hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      {/* AI Summary Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setAiModalOpen(false)}
          />
          <div
            className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl
            animate-slide-up flex flex-col overflow-hidden border border-gray-200"
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b border-gray-200
              bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">DPR Processed AI Summary</h2>
                  <p className="text-xs text-indigo-200">
                    {aiSummary?.model_used ? `Model: ${aiSummary.model_used}` : "Powered by AI"}
                    {aiSummary?.source === "cached" && " · Cached"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <FontAwesomeIcon
                      icon={faWandMagicSparkles}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 text-xl animate-pulse"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">Generating AI Summary...</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Analyzing filtered DPR processed data. This may take a few seconds.
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}

              {aiError && !aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faTimes} className="text-red-500 text-xl" />
                  </div>
                  <p className="text-sm text-red-600 font-medium">{aiError}</p>
                  <button
                    onClick={() => handleOpenAiSummary(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {aiSummary && !aiLoading && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Filtered Rows</p>
                      <p className="text-sm font-bold text-indigo-700">
                        {(aiSummary.filtered_total_rows || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Success</p>
                      <p className="text-sm font-bold text-emerald-700">
                        {(aiSummary.filtered_success_count || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Failed</p>
                      <p className="text-sm font-bold text-red-700">
                        {(aiSummary.filtered_failed_count || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Below Quota</p>
                      <p className="text-sm font-bold text-amber-700">
                        {(aiSummary.below_quota_count || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div
                    className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-headings:font-bold
                    prose-p:text-gray-700 prose-strong:text-gray-800 prose-ul:text-gray-700 prose-li:text-gray-700
                    prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-2 prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdownAsHtml(aiSummary.summary_text || ""),
                    }}
                  />
                </div>
              )}
            </div>

            {aiSummary && !aiLoading && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-500 flex flex-wrap gap-4">
                  <span>
                    Quota target/day: <strong>{aiSummary.quota_target || 272}</strong>
                  </span>
                  {aiSummary.prompt_tokens != null && (
                    <span>
                      Prompt tokens: <strong>{aiSummary.prompt_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                  {aiSummary.completion_tokens != null && (
                    <span>
                      Completion tokens: <strong>{aiSummary.completion_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                  {aiSummary.total_tokens != null && (
                    <span>
                      Total tokens: <strong>{aiSummary.total_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenAiSummary(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors duration-200 font-medium"
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={() => setAiModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg transition-colors duration-200 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DprUploadValidationCsvProcessedPage;

